/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License. See License.txt in the project root for
 * license information.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator 2.3.31.0
 * Changes may cause incorrect behavior and will be lost if the code is
 * regenerated.
 */

/* eslint-disable */
import { IDatabaseContent, IDataSource } from './database/interface';
import { IColumnRelationshipInformation, RelationshipType } from './relationship/interface';
import { SharedModel as Base, SharedModel } from './sharedmodel';

export module A365BackendModel {
    export const keyForJobAutoScaleMin = 'spark.dynamicAllocation.minExecutors';
    export const keyForJobAutoScaleMax = 'spark.dynamicAllocation.maxExecutors';

    export interface ModelWithFolder {
        folder?: {
            name: string;
        }
    }
    export interface NotebookResource extends Base.SubResource {
        properties: NotebookModel;
        [property: string]: any;
    }

    export interface NotebookModel extends ModelWithFolder {
        bigDataPool?: Base.ArtifactReference;
        targetSparkConfiguration?: Base.ArtifactReference;
        sessionProperties?: IComputeSessionProperties
        libraries?: string[];
        metadata: NotebookMetadata;
        nbformat: number;
        nbformat_minor: number;
        cells: NotebookCell[];
        description?: string;
    }

    interface NotebookMetadataCommon {
        kernelspec?: KernelSpec;
        language_info?: LanguageInfo;
        authors?: Author[];
    }

    /**
     * Notice: common metadata properties that have already been defined in https://github.com/jupyter/nbformat/blob/master/nbformat/v4/nbformat.v4.schema.json
     * should **NOT** be added to A365 specific. Otherwise, cross-notebook-app compatibility is hard to guarantee.
     */
    interface NotebookMetadataA365Specific {
        a365ComputeOptions?: ComputeOptions;
        sessionOptions?: IComputeSessionProperties;
        sessionKeepAliveTimeout?: number;
        targetSparkConfiguration?: string;
        [property: string]: any;
    }

    export type NotebookLanguage = 'python' | 'scala' | 'R' | 'csharp' | 'sql';

    export type NotebookMetadata = NotebookMetadataCommon & NotebookMetadataA365Specific;

    export interface NotebookSnapshot {
        id: string; // run id
        notebookContent: NotebookResource;
        notebook: string; // the name of the notebook
        sparkPool: string; // the name of the spark pool
        parameters: {
            [name: string]: {
                value: string;
                type: string;
            }
        }
    }

    export interface RunNotebookResult {
        snapshot: NotebookSnapshot;
        sessionId: string;
        runStatus: string;
    }

    export interface RunNotebookSnapshotResponse {
        result: RunNotebookResult;
    }

    export interface LanguageInfo {
        name: string;
        codemirror_mode?: any;
        version?: string;
    }

    export interface KernelSpec {
        name: string;
        display_name: string;
    }

    export interface Author {
        name: string;
    }

    export type NotebookCell = NotebookCodeCell | NotebookCellBase;

    export type NotebookCellType = 'code' | 'markdown' | 'raw';

    export interface NotebookCellBase {
        cell_type: NotebookCellType;
        metadata: NotebookCellMetadata;
        source: MultiLineString;
        attachments?: { [name: string]: OnDiskMediaBundle };
    }

    export interface NotebookCellMetadata {
        collapsed?: boolean;
        scrolled?: boolean | 'auto';
        deletable?: boolean;
        format?: string;
        name?: string;
        tags?: string[];
        [key: string]: any;
    }

    export interface NotebookCodeCell extends NotebookCellBase {
        cell_type: 'code';
        outputs: NotebookCodeCellOutput[];
        execution_count: number;
    }

    export type NotebookCodeCellOutput =
        NotebookCodeCellStreamOutput |
        NotebookCodeCellDisplayDataOutput |
        NotebookCodeCellErrorOutput |
        NotebookCodeCellExecuteResultOutput;

    export type NotebookCellOutput = 'stream' | 'display_data' | 'execute_result' | 'error';

    export interface NotebookCodeCellOutputBase {
        output_type: string;
        metadata?: OutputMetadata;
    }

    export interface NotebookCodeCellStreamOutput extends NotebookCodeCellOutputBase {
        output_type: 'stream';
        name: 'stdout' | 'stderr';
        text: MultiLineString;
    }

    export interface NotebookCodeCellDisplayDataOutput extends NotebookCodeCellOutputBase {
        output_type: 'display_data';
        data: OnDiskMediaBundle;
        metadata: OutputMetadata & MimeBundleMetadata;
    }

    export interface NotebookCodeCellExecuteResultOutput extends NotebookCodeCellOutputBase {
        output_type: 'execute_result';
        execution_count: number;
        data: OnDiskMediaBundle;
        metadata: OutputMetadata & MimeBundleMetadata;
    }

    export interface NotebookCodeCellErrorOutput extends NotebookCodeCellOutputBase {
        output_type: 'error';
        ename: string;
        evalue: string;
        traceback: string[];
    }

    export interface OutputMetadata {
        isolated?: string;
    }

    export interface SqlScriptResource extends Base.SubResource {
        properties: SqlScriptModel;
        [property: string]: any;
    }

    export interface ScriptMetadata {
        language?: string;
    }

    /**
     * @deprecated Use ScriptConnectionKeyV3 instead. This one will be deleted in future
     */
    export interface ScriptConnectionKey {
        name: string;
        type: 'SqlOnDemand' | 'SqlCompute' | 'SqlPool';
    }

    export interface ScriptConnectionKeyV3 {
        poolName: string;
        databaseName: string;
        poolType?: 'dedicated' | 'serverless' | 'unified'
    }

    export interface SqlScriptContent {
        query?: string;
        metadata?: ScriptMetadata;
        currentConnection?: ScriptConnectionKey | ScriptConnectionKeyV3;
        resultLimit: number;
    }

    export interface SqlScriptModel extends ModelWithFolder {
        description: string;
        content: SqlScriptContent;
        type: string;
    }

    export interface KqlScriptResource extends Base.SubResource {
        properties: KqlScriptModel;
        [property: string]: any;
    }

    export interface KqlScriptContent {
        query?: string;
        metadata?: ScriptMetadata;
        currentConnection?: ScriptConnectionKeyV3;
    }

    export interface KqlScriptModel extends ModelWithFolder {
        description: string;
        content: KqlScriptContent;
        type: string;
    }

    export interface Workspace extends Base.Resource {
        properties?: WorkspaceProperties;
        /**
         * @property Describes unknown properties. The value of an unknown property
         * can be of "any" type.
         */
        [property: string]: any;
    }

    // handcrafted
    export interface PurviewConfiguration {
        purviewResourceId?: string;
    }

    export interface ManagedVirtualNetworkSettings {
        preventDataExfiltration?: boolean;
        allowedAadTenantIdsForLinking?: string[];
    }

    export interface WorkspaceProperties {
        readonly provisioningState?: string;
        adlaResourceId?: string; // Used in soc workspace
        connectivityEndpoints?: ConnectivityEndpoints;
        cosmosVCSize?: number; // Used in soc workspace, the unit is token
        defaultDataLakeStorage?: DataLakeStorage;
        extraProperties?: WorkspaceFeatureConfigs; // Used to check if features like scope on synpase etc are enabled at workspace level
        managedResourceGroupName?: string;
        managedVirtualNetwork?: string;
        managedVirtualNetworkSettings?: ManagedVirtualNetworkSettings;
        purviewConfiguration?: PurviewConfiguration; // handcrafted
        sparkHistoryEndpoint?: ISparkHistoryEndpoint;
        sqlAdministratorLogin?: string;
        workspaceRepositoryConfiguration?: Base.RepoConfiguration;
        workspaceUID?: string; // Workspace identity, used in recent resources
        publicNetworkAccess?: 'Enabled' | 'Disabled'; // Enable or Disable public network access to workspace
        trustedServiceBypassEnabled?: boolean;
    }

    export interface WorkspaceFeatureConfigs {
        readonly IsScopeEnabled?: boolean;
        readonly WorkspaceType?: WorkspaceType
    }

    export interface ISparkHistoryEndpoint {
        endpoint: string;
        isPPEEnv: boolean;
    }

    // https://msdata.visualstudio.com/A365/_git/A365_RP?path=%2Fsrc%2Fcommon%2FMicrosoft.Analytics.Common%2FMetadataApiClient%2FContracts%2FWorkspace%2FWorkspaceDetails.cs&version=GBdeveloper&line=93&lineEnd=94&lineStartColumn=1&lineEndColumn=1&lineStyle=plain
    export type WorkspaceType =
        'Normal' // default
        | 'Migrated' // Brownfield
        | 'Connected' // WorkspaceLite
        | 'Disconnected'

    export interface ConnectivityEndpoints {
        readonly dev?: string;
        readonly sql?: string;
        readonly sqlOnDemand?: string;
        readonly web?: string;
    }

    export interface DataLakeStorage {
        readonly filesystem?: string;
        readonly accountUrl?: string;
    }

    export interface ScopeJobDefinitionResource extends Base.SubResource {
        properties: ScopeJobDefinitionModel;
        [property: string]: any;
    }

    export interface ScopeJobDefinitionModel extends ModelWithFolder {
        targetScopePool: Base.ArtifactReference;
        jobProperties: ScopeJobPropertiesModel;
    }

    export interface ScopeJobPropertiesModel {
        name: string;
        scriptPath: string;
        conf?: {
            [key: string]: string
        };
        refFiles?: string[]
        nebulaArguments?: string;
        scriptParameters?: {
            [key: string]: string
        };
        tags?: {
            [key: string]: string
        };
        runtimeName: string;
        parallelism?: number;
        parallelismPercent?: number;
        ecoTokenParallelism?: number;
        ecoTokenParallelismPercent?: number;
        defaultTokenPolicy?: DefaultTokenPolicy;
        notificationRecipients?: string;
        priority: number;
        maxUnavailability: number;
    }

    export type DefaultTokenPolicy = 'DefaultGuaranteedToken' | 'DefaultEcoToken' | 'DefaultMixedToken';

    export interface ScopeJobOperationResponse {
        operationResult: string;
        operationType?: string;
        cancelReason?: string;
        yieldReason?: string;
    }

    export interface SqlPool extends Base.Resource {
        properties?: SqlPoolProperties;
        sku?: SqlSku;
        kind?: string;
    }

    export interface SqlDatabase extends Base.Resource {
        properties?: SqlDatabaseProperties;
        systemData?: {
            createdAt?: string;
            createdBy?: string;
            createdByType?: string;
            lastModifiedAt?: string;
            lastModifiedBy?: string;
            lastModifiedByType?: string;
        }
    }
    export interface SqlDatabaseProperties {
        collation?: string;
        databaseGuid?: string;
        maxSizeBytes?: number;
        readonly status?: string;
        [property: string]: any;
    }

    export interface SqlSku {
        name?: string;
        tier?: string;
        capacity?: number;
    }

    export interface SqlPoolProperties {
        collation?: string;
        readonly creationDate?: string;
        maxSizeBytes?: number;
        readonly provisioningState?: string;
        restorePointInTime?: string;
        sourceDatabaseId?: string;
        readonly status?: string;
        recoverableDatabaseId?: string;
        createMode?: string;
        currentServiceObjectiveName?: string;
        [property: string]: any;
    }

    export interface SparkCompute extends Base.Resource {
        properties?: SparkComputeProperties;
    }

    export type NodeSize = 'None' | 'Small' | 'Medium' | 'Large' | 'XLarge' | 'XXLarge' | 'XXXLarge';

    export interface SparkComputeProperties {
        autoPause?: SparkAutoPause;
        autoScale?: SparkAutoScale;
        dynamicExecutorAllocation?: SparkDynamicExecutorAllocation;
        readonly creationDate?: string;
        nodeCount?: number;
        nodeSize?: NodeSize;
        nodeSizeFamily?: string;
        readonly provisioningState?: string;
        sparkVersion?: string;
        sparkEventsFolder?: string;
        libraryRequirements?: SparkLibraryRequirements;
        defaultSparkLogFolder?: string;
        isComputeIsolationEnabled?: boolean;
        sparkConfigProperties?: SparkLibraryRequirements;
        sessionLevelPackagesEnabled?: boolean;
        customLibraries?: SparkLibraryInfo[];
        [property: string]: any;
    }

    export interface SparkLibraryRequirements {
        time?: string;
        content?: string;
        filename?: string;
    }

    export interface SparkAutoPause {
        delayInMinutes?: number;
        enabled?: boolean;
    }

    export interface SparkAutoScale {
        enabled?: boolean;
        maxNodeCount?: number;
        minNodeCount?: number;
    }

    export interface SparkDynamicExecutorAllocation {
        enabled?: boolean;
        minExecutors?: number;
        maxExecutors?: number;
    }

    export interface SparkLibraryInfo {
        name?: string;
        path?: string;
        containerName?: string;
        uploadedTimestamp?: string;
        type?: string;
    }

    export interface SparkJobDefinitionResource extends Base.SubResource {
        properties: SparkJobDefinitionModel;
        [property: string]: any;
    }

    export interface SparkJobDefinitionModel extends ModelWithFolder {
        targetBigDataPool: Base.ArtifactReference; // used by Synapse RP
        targetSparkCompute: Base.ArtifactReference; // used by ProjectArcadia RP
        targetSparkConfiguration?: Base.ArtifactReference;
        requiredSparkVersion: string;
        language?: string;
        jobProperties: SparkJobPropertiesModel;
        scanFolder?: boolean;
    }

    export interface SparkJobPropertiesModel {
        name: string;
        file: string;
        className: string;
        conf?: {
            [keyForJobAutoScaleMin]: string;
            [keyForJobAutoScaleMax]: string;
            [key: string]: string
        };
        args?: string[];
        jars?: string[];
        pyFiles?: string[];
        files?: string[];
        archives?: string[];
        driverMemory: string;
        driverCores: number;
        executorMemory: string;
        executorCores: number;
        numExecutors: number;
    }


    export interface SparkConfigurationResource extends Base.Resource {
        properties: SparkConfigurationModel;
    }

    export interface SparkConfigurationModel {
        name: string;
        description?: string;
        notes?: string;
        configs: { [key: string]: string };
        createdBy?: string;
        created?: string;
        annotations?: string[];
        configMergeRule: { [key: string]: string };
        [property: string]: any;

    }

    export interface ComputeAuth {
        type: ComputeAuthType;
        authResource?: string;
        authHeader?: string;
    }

    export interface IComputeSessionProperties {
        driverMemory: string;
        driverCores: number;
        executorMemory: string;
        executorCores: number;
        numExecutors: number;
        runAsWorkspaceSystemIdentity?: boolean;
        conf: {
            [keyForJobAutoScaleMin]: string;
            [keyForJobAutoScaleMax]: string;
            [key: string]: string;
        };
    }

    export type ComputeAuthType = 'BASIC' | 'AAD';

    export interface ComputeOptions {
        id: string;
        type: string;
        name: string;
        endpoint: string;
        auth: ComputeAuth;
        sparkVersion?: string;
        nodeCount?: number;
        cores?: number;
        memory?: number;
        extraParams?: string;
        extraHeader?: any;
        automaticScaleJobs?: boolean;
    }

    export type MultiLineString = string | string[];

    export interface IJobListOnCosmosResponse {
        value?: JobOnCosmos[];
    }

    export interface JobOnCosmos {
        jobId: string;
        name: string;
        type: string;
        submitter: string;
        degreeOfParallelism: number;
        submitTime: Date;
        state: string;
    }

    export interface IActivitiesOnCosmosResponse {
        value?: IActivitiesResponse[];
    }

    export interface IActivitiesResponse {
        errorMessage: string;
        id: string;
        name: string;
        activityType: string; // spark | scope
        entityType: string; // resourcepools | batchjobs
        submitter: string;
        submitTime: Date;
        startTime: Date;
        state: string;
        analyticsUnits: number;
    }

    // Used in monitor -> cosmos job list
    export interface IActivities {
        errorMessage: string;
        id: string;
        name: string;
        activityType: string; // spark | scope
        entityType: string; // resourcepools | batchjobs
        submitter: string;
        submitTime: string;
        startTime: string;
        submitTimeDate: Date;
        state: string;
        analyticsUnits: number;
        statusIcon?: string;
        status?: string;
    }

    /**
     * Media Bundles as they exist on disk from the notebook format
     * See https://nbformat.readthedocs.io/en/latest/format_description.html#display-data for docs
     * and https://github.com/jupyter/nbformat/blob/master/nbformat/v4/nbformat.v4.schema.json for the schema
     */
    export interface OnDiskMediaBundle {
        "text/plain"?: MultiLineString;
        "text/html"?: MultiLineString;
        "text/latex"?: MultiLineString;
        "text/markdown"?: MultiLineString;
        "application/javascript"?: MultiLineString;
        "image/png"?: MultiLineString;
        "image/jpeg"?: MultiLineString;
        "image/gif"?: MultiLineString;
        "image/svg+xml"?: MultiLineString;
        "application/json"?: string | string[] | {};
        "application/vdom.v1+json"?: {};
        "application/vnd.dataresource+json"?: {};
        "text/vnd.plotly.v1+html"?: MultiLineString | {};
        "application/vnd.plotly.v1+json"?: {};
        "application/geo+json"?: {};
        "application/x-nteract-model-debug+json"?: {};
        "application/vnd.vega.v2+json"?: {};
        "application/vnd.vega.v3+json"?: {};
        "application/vnd.vegalite.v1+json"?: {};
        "application/vnd.vegalite.v2+json"?: {};
        [key: string]: string | string[] | {} | undefined;
    }
    export interface MediaBundle {
        "text/plain"?: string;
        "text/html"?: string;
        "text/latex"?: string;
        "text/markdown"?: string;
        "application/javascript"?: string;
        "image/png"?: string;
        "image/jpeg"?: string;
        "image/gif"?: string;
        "image/svg+xml"?: string;
        "application/json"?: {
            [key: string]: any;
        };
        "application/vdom.v1+json"?: {
            [key: string]: any;
        };
        "application/vnd.dataresource+json"?: {
            [key: string]: any;
        };
        "text/vnd.plotly.v1+html"?: string | {
            [key: string]: any;
        };
        "application/vnd.plotly.v1+json"?: {
            [key: string]: any;
        };
        "application/geo+json"?: {
            [key: string]: any;
        };
        "application/x-nteract-model-debug+json"?: {
            [key: string]: any;
        };
        "application/vnd.vega.v2+json"?: {
            [key: string]: any;
        };
        "application/vnd.vega.v3+json"?: {
            [key: string]: any;
        };
        "application/vnd.vegalite.v1+json"?: {
            [key: string]: any;
        };
        "application/vnd.vegalite.v2+json"?: {
            [key: string]: any;
        };
        [key: string]: string | string[] | {} | undefined;
    }

    export type MimeBundleMetadata = {
        [mime in keyof OnDiskMediaBundle]: any;
    };

    /**
     * @class
     * Initializes a new instance of the SynapseStoredProcedureActivityTypeProperties class.
     * @constructor
     * SQL stored procedure activity properties.
     *
     * @member {object} storedProcedureName Stored procedure name. Type: string (or
     * Expression with resultType string).
     * @member {object} [storedProcedureParameters] Value and type setting for
     * stored procedure parameters. Example: "{Parameter1: {value: "1", type:
     * "int"}}".
     */
    export interface SynapseStoredProcedureActivityTypeProperties {
        storedProcedureName: any;
        storedProcedureParameters?: { [propertyName: string]: Base.StoredProcedureParameter };
    }

    export interface SynapseNotebookParameter {
        value: any;
        type: string;
    }

    export interface SynapseNotebookActivityTypeProperties {
        notebook: Base.ArtifactReference;
        parameters?: { [propertyName: string]: SynapseNotebookParameter };
        snapshot?: boolean;
        sparkPool?: Base.ArtifactReference;
        executorSize?: NodeSize;
        conf?: {
            'spark.dynamicAllocation.enabled': boolean,
            'spark.dynamicAllocation.minExecutors': number,
            'spark.dynamicAllocation.maxExecutors': number
        };
        driverSize?: NodeSize;
        numExecutors?: number;
        authentication? : SynapseActivityAuthentication;
        configurationType?: ConfigurationType;
        targetSparkConfiguration?: Base.ArtifactReference;
        sparkConfig?: { [propertyName: string]: any };
    }

    export interface SynapseNotebookActivity extends Base.ExecutionActivity {
        typeProperties: SynapseNotebookActivityTypeProperties;
    }

    export interface SynapseSparkJobDefinitionActivityTypeProperties {
        sparkJob: Base.ArtifactReference;
        file?: string;
        className?: string;
        args?: { [propertyName: string]: any };
        scanFolder?: boolean;
        files?: { [propertyName: string]: any };
        pythonCodeReference?: { [propertyName: string]: any };
        filesV2?: { [propertyName: string]: any };
        targetBigDataPool?: Base.ArtifactReference;
        executorSize?: NodeSize;
        conf?: {
            'spark.dynamicAllocation.enabled': boolean,
            'spark.dynamicAllocation.minExecutors': number,
            'spark.dynamicAllocation.maxExecutors': number
        };
        driverSize?: NodeSize;
        numExecutors?: number;
        authentication? : SynapseActivityAuthentication;
        configurationType?: ConfigurationType;
        targetSparkConfiguration?: Base.ArtifactReference;
        sparkConfig?: { [propertyName: string]: any };
    }

    export interface SynapseSparkJobDefinitionActivity extends Base.ExecutionActivity {
        typeProperties: SynapseSparkJobDefinitionActivityTypeProperties;
    }

    export type ConfigurationType = 'Default' | 'Customized' | 'Artifact';

    export type SynapseActivityAuthType = 'MSI' | 'UserAssignedManagedIdentity';

    export interface SynapseActivityAuthentication {
        type?: SynapseActivityAuthType;
        credential?: SharedModel.CredentialReference;
    }
    
    /**
     * @class
     * Initializes a new instance of the AzureSynapseArtifactsLinkedServiceTypeProperties class.
     * @constructor
     * Azure Synapse Analytics artifacts linked service properties.
     *
     * @member {object} endpoint Azure Synapse workspace URL.
     * Type: string (or Expression with resultType string).
     */
    export interface AzureSynapseArtifactsLinkedServiceTypeProperties {
        endpoint: string;
        authentication?: any;
        workspaceResourceId?: any;
        credential?: SharedModel.CredentialReference;
    }

    /**
     * @class
     * Initializes a new instance of the AzureSynapseArtifactsLinkedService class.
     * @constructor
     * Azure Synapse Analytics artifacts linked service.
     *
     * @member {object} typeProperties Azure Synapse Analytics linked service
     * properties.
     * @member {object} [typeProperties.endpoint] Azure Synapse workspace URL.
     * Type: string (or Expression with resultType string).
     */
    export interface AzureSynapseArtifactsLinkedService extends SharedModel.LinkedService {
        typeProperties: AzureSynapseArtifactsLinkedServiceTypeProperties;
    }

    export interface SynapseScopeJobDefinitionActivityTypeProperties {
        scopeJob: Base.ArtifactReference;
        scriptParameters?: { [propertyName: string]: any };
        priority?: number;
        maxUnavailability?: number;
        tags?: { [propertyName: string]: any };
        nebulaArguments?: string;
        token?: number;
        tokenPercentage?: number;
        ecoToken?: number;
        ecoTokenPercentage?: number;
    }

    export interface SynapseScopeJobDefinitionActivity extends Base.ExecutionActivity {
        typeProperties: SynapseScopeJobDefinitionActivityTypeProperties;
    }

    export interface SynapseStoredProcedureActivity extends Base.ExecutionActivity {
        typeProperties: SynapseStoredProcedureActivityTypeProperties;
        sqlPool: Base.ArtifactReference;
    }

    export interface SparkJob {
        state?: string;
        name?: string;
        submitter?: string;
        submitTime?: string;
        submitTimeDate?: Date;
        compute?: string;
        sparkPoolName?: string;
        sparkApplicationId?: string;
        livyId?: number;
        timing: string[];
        endTime?: string;
        startTime?: string;
        endTimeDate?: Date;
        startTimeDate?: Date;
        queuedDuration?: string;
        runningDuration?: string;
        totalDuration?: string;
        sparkJobDefinition?: string[];
        pipeline?: string[];
        jobType?: string;
        statusIcon?: string;
    }

    export interface SparkJobResponse {
        state?: string;
        name?: string;
        submitter?: string;
        submitTime?: Date;
        compute?: string;
        sparkPoolName?: string;
        sparkApplicationId?: string;
        livyId?: number;
        timing: string[];
        endTime?: any;
        startTime?: Date;
        queuedDuration?: string;
        runningDuration?: string;
        totalDuration?: string;
        sparkJobDefinition?: string[];
        pipeline?: string[];
        jobType?: string;
    }

    export interface SQLRequestStep {
        request_id?: string;
        step_index?: number;
        operation_type?: string;
        distribution_type?: string;
        location_type?: string;
        status?: string;
        error_id?: string;
        start_time?: string;
        end_time?: string;
        total_elapsed_time?: string;
        row_count?: string;
        command?: string;
    }

    export interface SQLJob {
        status?: string;
        requestId?: string;
        sessionId?: string;
        duration?: string;
        dataProcessed?: string;
        submitter?: string;
        poolName?: string;
        workloadGroup?: string;
        importance?: string;
        classifier?: string;
        submitTime?: string;
        endTime?: string;
        queuedDuration?: string;
        command?: string;
        runningDuration?: string;
        statusIcon?: string;
        total_count?: string;
        submitTimeDate?: Date;
        endTimeDate?: Date;
        startTimeDate?: Date;
    }

    export interface LinkTopicStatus {
        name?: string;
        id?: string;
        status?: 'Initial' | 'Starting' | 'Running' | 'Stopping' | 'Stopped';
        error?: any;
        startTime?: string;
        stopTime?: string;
        lastReplicatedTime?: string;
        dataProcessed?: number;
        continuousRunId?: string;
        isPartiallyFailed?: boolean;
        landingZoneCredentialExpireTime?: string;
    }

    export interface LinkTableStatus {
        id: string;
        LinkTableId?: string;
        linkTableId?: string;
        SourceTableName?: string;
        sourceTableName?: string;
        SourceSchemaName?: string;
        sourceSchemaName?: string;
        TargetTableName?: string;
        targetTableName?: string;
        TargetSchemaName?: string;
        targetSchemaName?: string;
        Status?: 'Snapshotting' | 'Replicating' | 'Failed';
        status?: 'Snapshotting' | 'Replicating' | 'Failed';
        ErrorMessage?: string;
        errorMessage?: string;
        lastProcessedData?: string;
        lastTransactionCommitTime?: string;
        processedRowCount?: number;
        processedDataVolume?: number;
    }

    export interface KQLJob {
        requestId: string;
        command: string;
        duration: string;
        submitter: string;
        status: string;
        poolName: string;
        database: string;
        application: string;
        workloadGroup: string;
        startTimeDate: Date;
        endTimeDate: Date;
        failureReason?: string;
    }

    export interface SQLStreamingJob {
        name: string;
        status: string;
        statusIcon?: string;
        submitTime: string;
        outputStartTime: string;
        lastOutputTime: string;
        streamingUnits: number;
        poolName: string;
        databaseName: string;
        submitter: string;
        submitTimeDate?: Date;
        outputStartTimeDate?: Date;
        lastOutputTimeDate?: Date;
    }

    export type SQLStreamingJobStatus = 'Created' | 'Starting' | 'Running' | 'Stopping' | 'Stopped' | 'Deleting' | 'Failed' | 'Degraded' | 'Restarting' | 'Scaling'
        | 'created' | 'starting' | 'running' | 'stopping' | 'stopped' | 'deleting' | 'failed' | 'degraded' | 'restarting' | 'scaling';

    export interface RawSqlStreamingJobDataModel extends BaseAMSResponse {
        jobName?: string;
        jobStatus?: {
            status: SQLStreamingJobStatus,
            error: string,
        };
        streamingUnits?: number;
        sqlPoolName?: string;
        sqlDatabaseName?: string;
        submitter?: string;
        submitTimeUtc?: string;
        outputStartTimeUtc?: string;
        lastOutputTimeUtc?: string;
    }

    // Model RawSqlStreamingJobQueryResponse will be removed once SQL-OD is accessible to AMS
    export interface RawSqlStreamingJobQueryResponse {
        isSuccess: boolean;
        recordsAffected: number;
        message: string;
        errorCode: number;
        schema: Schema[];
        data: string[][];
        errors: string[];
    }

    export interface AMSErrorResponse {
        opertaion_id: string;
        error_number: string;
        error_description: string;
    }

    export interface BaseAMSResponse {
        baseError?: AMSErrorResponse;
    }

    export interface ISparkJobListResponse extends BaseAMSResponse {
        nJobs?: string;
        sparkJobs?: [];
    }

    export interface ISQLJobListResponse extends BaseAMSResponse {
        nQueries?: string;
        sqlJobs?: [];
    }

    export interface ISQLStreamingJobListResponse extends BaseAMSResponse {
        value?: RawSqlStreamingJobDataModel[];
    }

    export interface MonitoringSqlPool {
        sqlPoolName: string;
        status?: string;
        resourceAllocation?: string;
        dwuUsage?: string;
        size?: string;
        tags?: string;
        submitTime?: string;
        endTime?: string;
        startTime?: string;
        endTimeDate?: Date;
        startTimeDate?: Date;
        submitTimeDate?: Date;
    }

    export interface ISparkPoolListResponse extends BaseAMSResponse {
        nPools?: string;
        sparkPools?: [];
    }

    export interface ISqlPoolListResponse extends BaseAMSResponse {
        nPools?: string;
        sqlPools?: [];
    }

    export interface IKustoPoolListResponse extends BaseAMSResponse {
        value: IKustoPool[];
    }

    export interface IKustoPool {
        poolName: string;
        status: string;
        size: string;
        poolStatusIcon: string;
        instanceCount: string;
        vCPUs: string;
        cache: string;
        ram: string;
    }

    /* eslint-disable @typescript-eslint/no-explicit-any */
    export interface IMonitoringKustoPoolDetails {
        instanceCountMetric: string;
        cpuMetric: string;
        cacheUtilizationMetic: string;
        ingestionUtilizationMetic: string;
        instanceCountChartsMetric?: any;
        queryResultsChartsMetric?: any;
        ingestionResultsChartsMetric?: any;
    }

    export interface IScopeJobListResponse extends BaseAMSResponse {
        nJobs?: string;
        scopeJobs?: [];
        value?: []; // used in scope job in cosmos
    }

    export class Schema {
        public columnName: string;
        public ordinal: number;
        public dataTypeName: string;
    }

    export interface RawSqlJobDataModel extends BaseAMSResponse {
        isSuccess: boolean;
        recordsAffected: number;
        message: string;
        errorCode: number;
        schema: Schema[];
        data: string[][];
        errors: string[];
    }
    export class SqlErrorDetails {
        public isSuccess: boolean;
        public recordsAffected: number;
        public message: string;
        public errorCode: number;
        public schema: Schema[];
        public data: string[][];
        public errors: string[];
    }

    export class SQLErrorResponse {
        public headers: string;
        public ok: boolean;
        public status: number;
        public statusText: string;
        public url: string;
        public _body: string;
    }

    export interface ScopeJob {
        state?: string;
        name?: string;
        submitter?: string;
        submitTime?: string;
        submitTimeDate?: Date;
        scopePoolName?: string;
        jobId?: string;
        endTime?: string;
        startTime?: string;
        endTimeDate?: Date;
        startTimeDate?: Date;
        queuedDuration?: string;
        runningDuration?: string;
        totalDuration?: string;
        statusIcon?: string;
        priority?: number;
        requestedResource?: number;
    }

    export interface ScopeJobResponse {
        state?: string;
        name?: string;
        submitter?: string;
        submitTime?: Date;
        pool?: string;
        jobId?: string;
        endTime?: Date;
        startTime?: Date;
        queuedDuration?: string;
        runningDuration?: string;
        totalDuration?: string;
        priority?: number;
        requestedResource?: number;
    }

    // Model to format the Spark Pool size
    export interface ISparkPoolSize {
        isAutoScaleEnabled: boolean;
        maxNodeCount: number;
        minNodeCount: number;
        nodeCount: number;
        size: string;
    }

    // Model to format the SparkPool List view backend call.
    export interface SparkPool {
        poolName: string;
        numActiveInstances: string;
        memoryAllocation: string;
        createdOn: string;
        cpuAllocation: string;
        formattedSizeStr: string;
        size: ISparkPoolSize;
        formattedNodesStr: string;
    }

    // UX Model for SQL Pool list
    export interface SqlPoolModel {
        poolName: string;
        poolType: string;
        poolStatus: string;
        poolStatusIcon: string;
        poolSize: string;
        createdOn: string;
        cpuUtilization: string;
        memoryUtilization: string;
        kind: string;
    }

    export interface LinkTopicResource {
        name: string;
        properties: {
            description?: string;
            id?: string;
            sourceDatabase: {
                linkedService: Base.LinkedServiceResource | Base.LinkedServiceReference;
                typeProperties: {
                    resourceId: string;
                    principalId: string;
                }
            },
            targetDatabase: {
                linkedService: Base.LinkedServiceResource | Base.LinkedServiceReference;
                typeProperties: {
                    crossTableTransaction: boolean;
                    dropExistingTargetTableOnStart: boolean;
                    actionOnExistingTargetTable: 'FailOnNonEmptyTable' | 'DropAndRecreateTable' | 'MergeWithExistingData';
                }
            },
            landingZone?: {
                linkedService: Base.LinkedServiceResource | Base.LinkedServiceReference;
                sasToken?: string | Base.SecretBase;
                fileSystem?: string;
                folderPath?: string;
                encryptedCredential?: any;
            },
            compute: {
                coreCount: number;
                computeType: string;
                dataProcessIntervalInMinutes: number;
            }
        }
    }

    export interface LinkTableResource {
        id: string,
        source: {
            tableName: string,
            schemaName: string
        },
        target: {
            tableName: string,
            schemaName: string,
            distributionOptions: {
                type: string,
                distributionColumn?: string
            },
            structureOptions?: {
                type: SharedModel.TableStructureType
            },
            partitionOptions?: {
                partitionColumns: string[]
            }
        }
    }

    export interface DatabaseResource extends Base.SubResource {
        properties: IDatabaseContent;
        [property: string]: any; // Key named payload is used in publish
    }

    export interface TableResource extends Base.SubResource {
        properties: IRawTableContent;
        [property: string]: any; // Key named payload is used in publish
    }

    export interface IRawTableContent {
        name: string;
        description: string;
        storageDescriptor: IRawStorageDescriptor;
        namespace: IRawNamespace,
        tableType: string;
        objectId: string;
        partitioning: IRawPartitionInfo;
        properties?: {
            [propName: string]: string | boolean;
        };
        [propName: string]: any;
    }

    export interface IRawNamespace {
        DatabaseName: string;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [propName: string]: any;
    }

    export interface IRawStorageDescriptor {
        columns?: IRawColumn[];
        format?: IRawFormatInfo;
        source?: IDataSource;
        properties?: {
            'textinputformat.record.delimiter'?: string;
            compression?: string;
            derivedModelAttributeInfo: string;
            [propName: string]: any;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [propName: string]: any;
    }

    export interface IRawPartitionInfo {
        partitionFunctionType: string;
        keys: string[];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [propName: string]: any;
    }

    export interface IRawColumn {
        name: string;
        originDataTypeName: IRawOriginDataTypeName;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [propName: string]: any;
    }

    export interface IRawOriginDataTypeName {
        typeName: string;
        isComplexType: boolean;
        isNullable: boolean;
        length?: number;
        precision?: number;
        scale?: number;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        properties?: { [key: string]: any };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [propName: string]: any;
    }

    export interface IRawFormatInfo {
        formatType: 'csv' | 'parquet' | 'delta';
        inputFormat: string;
        outputFormat: string;
        serializeLib: string;
        properties: {
            path?: string;
            delimiter?: string;
            firstRowAsHeader?: string;
            multiLine?: string;
            serializationFormat?: string;
            escape?: string;
            quote?: string;
            [propName: string]: any;
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [propName: string]: any;
    }

    export interface RelationshipResource extends Base.SubResource {
        properties: IRawRelationshipContent;
        [property: string]: any; // Key named payload is used in publish
    }

    export interface IRawRelationshipContent {
        name: string;
        description: string;
        namespace: IRawNamespace,
        relationshipType: RelationshipType;
        objectId: string;
        fromTableName: string;
        toTableName: string;
        columnRelationshipInformations: IColumnRelationshipInformation[];
        properties?: {
            [propName: string]: string;
        };
        [propName: string]: any;
    }
}
